import { useCallback, useEffect, useRef } from 'react';
import { useBrowserLocation } from 'wouter/use-browser-location';
import { usePatientTabs } from './patient-tabs.js';

interface NavigateOptions {
  replace?: boolean;
}

/**
 * Source de localisation du routeur interne. Toute navigation applicative passe par ici : si
 * une région est sale, elle est retenue et le dialogue de garde décide. Le retour navigateur
 * (`popstate`) est traité à part, parce qu'il a déjà changé l'URL quand on l'apprend : on la
 * repousse alors avant d'ouvrir le dialogue, exactement comme en V1.
 */
export function useGuardedLocation(): [string, (to: string, options?: NavigateOptions) => void] {
  const [location, navigate] = useBrowserLocation();
  const { requestNavigation } = usePatientTabs();
  const previous = useRef(location);

  const guardedNavigate = useCallback(
    (to: string, options?: NavigateOptions) => {
      const commit = () => {
        navigate(to, options);
      };
      if (!requestNavigation(to, commit)) commit();
    },
    [navigate, requestNavigation],
  );

  useEffect(() => {
    const onPopState = () => {
      const target = globalThis.location.pathname + globalThis.location.search;
      const restore = previous.current;
      const blocked = requestNavigation(target, () => {
        globalThis.history.pushState(null, '', target);
        navigate(target, { replace: true });
      });
      if (blocked) {
        globalThis.history.pushState(null, '', restore);
        navigate(restore, { replace: true });
      }
    };
    globalThis.addEventListener('popstate', onPopState);
    return () => {
      globalThis.removeEventListener('popstate', onPopState);
    };
  }, [navigate, requestNavigation]);

  useEffect(() => {
    previous.current = location;
  }, [location]);

  return [location, guardedNavigate];
}
